import { useState } from 'react';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "../../apis/axios";
import { getToken } from "../../utilities";

/**
 * Custom hook to upload multiple files serially (one at a time)
 * @param {Object} options Configuration options
 * @param {string} options.url API endpoint for upload
 * @param {string} options.refreshUrl URL to invalidate in query cache after upload
 * @param {Function} options.onSuccess Callback when all uploads complete successfully
 * @param {Function} options.onError Callback when an error occurs
 * @param {Function} options.onFileSuccess Callback when each individual file upload succeeds
 * @param {Function} options.onFileError Callback when each individual file upload fails
 */
const useSerialUpload = ({ 
  url, 
  refreshUrl = "", 
  onSuccess, 
  onError,
  onFileSuccess,
  onFileError 
}) => {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [uploadedResults, setUploadedResults] = useState([]);

  // Function to upload a single file
  const uploadSingleFile = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    const response = await axios({
      method: "POST",
      data: formData,
      url: `/${url}`,
      headers: { Authorization: getToken() ? `Bearer ${getToken()}` : "" },
    });
    
    return response.data;
  };

  // Create a mutation that processes files one by one
  const serialUpload = useMutation(
    async ({ files, type }) => {
      setUploadedResults([]);
      setProgress(0);
      
      const results = [];
      
      for (let i = 0; i < files.length; i++) {
        try {
          const result = await uploadSingleFile(files[i], type);
          results.push(result);
          setUploadedResults(prev => [...prev, result]);
          onFileSuccess?.(result, i, files[i]);
          // Update progress
          setProgress(Math.round(((i + 1) / files.length) * 100));
        } catch (error) {
          // If one file fails, we still try the rest
          console.error(`Error uploading file ${i}:`, error);
          onFileError?.(error, i, files[i]);
        }
      }
      
      return results;
    },
    {
      onSuccess: (data) => {
        if (refreshUrl) {
          queryClient.invalidateQueries(refreshUrl);
        }
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );
  
  return {
    upload: serialUpload,
    progress,
    uploadedResults,
    isUploading: serialUpload.isLoading
  };
};

export default useSerialUpload;
